import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useSettings } from '../lib/hooks/useSettings';

const Header = () => {
  const { settings } = useSettings();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNavigator, setShowNavigator] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkForFloorPlans = async () => {
      try {
        const q = query(collection(db, 'floorPlans'));
        const querySnapshot = await getDocs(q);
        setShowNavigator(querySnapshot.size > 0);
      } catch (error) {
        console.error('Error checking floor plans:', error);
        setShowNavigator(false);
      }
    };

    checkForFloorPlans();
  }, []);
  const isHomePage = location.pathname === '/';

  const handleScrollTo = (elementId: string) => {
    setIsMobileMenuOpen(false);
    if (!isHomePage) {
      navigate('/', { state: { scrollTo: elementId } });
      return;
    }
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const targetId = location.state?.scrollTo;
    if (!targetId) return;

    // Homepage sections load asynchronously and images above the target keep
    // shifting its position while they load. So wait until the target exists
    // AND its document position stays stable before scrolling — otherwise we
    // land in the wrong spot after later content pushes the section down.
    let attempts = 0;
    const maxAttempts = 80; // ~8s at 100ms
    let lastTop: number | null = null;
    let lastDocH: number | null = null;
    let stableCount = 0;
    let timer: ReturnType<typeof setTimeout>;

    const finish = (element: Element | null) => {
      if (element) element.scrollIntoView({ behavior: 'smooth' });
      navigate(location.pathname, { replace: true, state: {} });
    };

    const check = () => {
      const element = document.getElementById(targetId);
      const docH = document.body.scrollHeight;
      if (element) {
        const top = Math.round(element.getBoundingClientRect().top + window.scrollY);
        // Require BOTH the section position and the total page height to be
        // stable: content loading below the target can otherwise clamp the
        // scroll short until the page finishes growing.
        const stable =
          lastTop !== null &&
          Math.abs(top - lastTop) < 2 &&
          lastDocH !== null &&
          Math.abs(docH - lastDocH) < 2;
        stableCount = stable ? stableCount + 1 : 0;
        lastTop = top;
        lastDocH = docH;
        if (stableCount >= 3) {
          finish(element);
          return;
        }
      } else {
        lastDocH = docH;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        timer = setTimeout(check, 100);
      } else {
        finish(element);
      }
    };

    timer = setTimeout(check, 100);
    return () => clearTimeout(timer);
  }, [location.state, location.pathname, navigate]);

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled || !isHomePage ? 'bg-primary shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between">
          <a
            href="/"
            onClick={handleHomeClick}
            className="flex items-center space-x-3"
          >
            {settings.logoUrl ? (
              <img
                src={settings.logoUrl}
                alt={settings.companyName}
                className="h-10 md:h-12 w-auto object-contain"
              />
            ) : (
              <div className="text-2xl font-bold text-white">
                <span className="text-secondary">{settings.companyName}</span>
              </div>
            )}
          </a>

          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/apartments"
              className="text-white hover:text-secondary transition duration-300"
            >
              {settings.catalogLabel}
            </Link>
            
            <Link
              to="/gallery"
              className="text-white hover:text-secondary transition duration-300"
            >
              Галерия
            </Link>
            
            {showNavigator && (
              <Link
                to="/navigator"
                className="text-white hover:text-secondary transition duration-300"
              >
                Разпределения
              </Link>
            )}

            <button
              onClick={() => handleScrollTo('location')}
              className="text-white hover:text-secondary transition duration-300"
            >
              Локация
            </button>
            
            <button
              onClick={() => handleScrollTo('contact')}
              className="text-white hover:text-secondary transition duration-300"
            >
              Контакти
            </button>
            
            <a
              href={`tel:${(settings.phone || '').replace(/\s+/g, '')}`}
              className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-dark transition duration-300"
            >
              <Phone size={18} />
              <span>{settings.phone}</span>
            </a>
          </div>

          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-primary shadow-lg py-4">
            <div className="flex flex-col space-y-4 px-4">
              <Link
                to="/apartments"
                className="text-white hover:text-secondary transition duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {settings.catalogLabel}
              </Link>
              
              <Link
                to="/gallery"
                className="text-white hover:text-secondary transition duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Галерия
              </Link>
              
              {showNavigator && (
                <Link
                  to="/navigator"
                  className="text-white hover:text-secondary transition duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Разпределения
                </Link>
              )}

              <button
                onClick={() => handleScrollTo('location')}
                className="text-white hover:text-secondary transition duration-300 text-left"
              >
                Локация
              </button>
              
              <button
                onClick={() => handleScrollTo('contact')}
                className="text-white hover:text-secondary transition duration-300 text-left"
              >
                Контакти
              </button>
              
              <a
                href={`tel:${(settings.phone || '').replace(/\s+/g, '')}`}
                className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary-dark transition duration-300"
              >
                <Phone size={18} />
                <span>{settings.phone}</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;